<template>
  <Dialog
    :open="show"
    @update:open="(v: boolean) => emit('update:show', v)"
  >
    <DialogContent class="flex max-h-[80vh] flex-col overflow-hidden sm:max-w-[540px]">
      <DialogHeader class="px-4 pb-3">
        <DialogTitle class="text-base font-semibold">{{ t('title') }}</DialogTitle>
      </DialogHeader>

      <ScrollArea class="px-4 pb-4">
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-end gap-2">
            <label class="text-sm font-medium">{{ t('checkLabel') }}</label>
            <div class="w-28">
              <SelectField
                v-model="selectedPersonType"
                :options="personTypeOptions"
                name="personType"
                @update:model-value="(value: string) => (selectedPersonType = value)"
              />
            </div>
          </div>

          <div
            v-if="!hasCheckedCollision"
            class="space-y-3"
          >
            <div class="rounded-md bg-gray-50 p-3">
              <div class="flex flex-wrap gap-2">
                <div class="min-w-[150px] flex-1">
                  <InputField
                    v-model="payload.firstName"
                    :label="t('firstName')"
                    name="firstName"
                    type="text"
                  />
                </div>
                <div class="min-w-[150px] flex-1">
                  <InputField
                    v-model="payload.lastName"
                    :label="t('lastName')"
                    name="lastName"
                    type="text"
                  />
                </div>
              </div>

              <div
                v-if="selectedPersonType === 'client'"
                class="mt-2 flex flex-wrap gap-2"
              >
                <div class="min-w-[150px] flex-1">
                  <InputField
                    v-model="payload.dateOfBirth"
                    :label="t('dateOfBirth')"
                    name="dateOfBirth"
                    type="date"
                  />
                </div>
                <div class="min-w-[150px] flex-1">
                  <InputField
                    v-model="payload.email"
                    :label="t('email')"
                    name="email"
                    type="email"
                  />
                </div>
              </div>

              <div class="mt-2 grid grid-cols-12 gap-2">
                <div class="col-span-7">
                  <InputField
                    v-model="payload.street"
                    :label="t('street')"
                    name="street"
                    type="text"
                  />
                </div>
                <div class="col-span-5">
                  <InputField
                    v-model="payload.streetNumber"
                    :label="t('houseNumber')"
                    name="streetNumber"
                    type="text"
                  />
                </div>
              </div>

              <div class="mt-2 grid grid-cols-12 gap-2">
                <div class="col-span-4">
                  <InputField
                    v-model="payload.zip"
                    :label="t('zip')"
                    name="zip"
                    type="text"
                  />
                </div>
                <div class="col-span-8">
                  <InputField
                    v-model="payload.city"
                    :label="t('city')"
                    name="city"
                    type="text"
                  />
                </div>
              </div>
            </div>

            <Button
              type="button"
              class="h-8 w-full"
              :disabled="loading"
              @click.prevent="checkCollision"
            >
              <Loader2
                v-if="loading"
                class="mr-2 h-4 w-4 animate-spin"
              />
              {{ t('checkConflict') }}
            </Button>
          </div>

          <div
            v-else
            class="space-y-3"
          >
            <div
              v-if="collisionResponseError"
              class="space-y-2"
            >
              <p class="text-sm text-red-600">
                {{ t('error') }}
              </p>
              <Button
                type="button"
                class="h-8 w-full"
                @click="resetForm"
              >
                {{ t('tryAgain') }}
              </Button>
            </div>

            <div v-else>
              <div
                v-if="collisionResponse?.collisions?.length"
                class="space-y-3"
              >
                <p class="text-sm font-medium text-red-600">
                  {{ t('warning') }}
                </p>
                <div class="max-h-[220px] overflow-y-auto rounded-md border">
                  <table class="w-full text-xs">
                    <thead class="sticky top-0 bg-gray-50">
                      <tr class="border-b">
                        <th class="p-2 text-left font-medium text-gray-600">
                          {{ t('caseFile') }}
                        </th>
                        <th class="p-2 text-left font-medium text-gray-600">
                          {{ t('name') }}
                        </th>
                        <th class="p-2 text-left font-medium text-gray-600">
                          {{ t('role') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(c, idx) in collisionResponse.collisions"
                        :key="idx"
                        class="border-b"
                      >
                        <td class="p-2">
                          <a
                            :href="`https://app.actaport.de/akte/${c.caseId.replace('/', '-')}/dashboard`"
                            target="_blank"
                            class="text-blue-600 hover:underline"
                          >
                            {{ c.caseId }}
                          </a>
                        </td>
                        <td class="p-2">{{ c.contact }}</td>
                        <td class="p-2">{{ c.role }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Button
                  type="button"
                  class="h-8 w-full"
                  @click="resetForm"
                >
                  {{ t('checkAnother') }}
                </Button>
              </div>

              <div
                v-else
                class="space-y-3 text-center"
              >
                <p class="text-sm text-green-600">
                  {{ t('noConflict') }}
                </p>
                <Button
                  type="button"
                  class="mx-auto h-8 w-full max-w-[200px]"
                  @click="resetForm"
                >
                  {{ t('checkAnother') }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import ApiService from '@/services/api.service'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useTranslation } from '@/composables/useTranslation'
  import { Loader2 } from 'lucide-vue-next'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { ScrollArea } from '@/components/ui/scroll-area'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import InputField from '@/components/admin/form/InputField.vue'

  interface Props {
    show: boolean
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'update:show', value: boolean): void
  }>()

  // Translation
  const { t } = useTranslation({
    en: {
      title: 'Check for Conflict of Interest',
      firstName: 'First Name',
      lastName: 'Last Name',
      dateOfBirth: 'Date of Birth',
      email: 'Email',
      street: 'Street',
      houseNumber: 'House Number',
      zip: 'Postal Code',
      city: 'City',
      checkConflict: 'Check for Conflict of Interest',
      warning: 'Warning: There might be a conflict of interest.',
      noConflict: 'No conflict of interest found based on the provided parameters.',
      error:
        'An error occurred while querying Actaport. Please wait a moment and try again. If the error persists, please contact support at support@jupus.de.',
      caseFile: 'Case File',
      name: 'Name',
      role: 'Role',
      checkAnother: 'Check Another',
      tryAgain: 'Try Again',
      checkLabel: 'Check:',
      client: 'Client',
      opponent: 'Opponent'
    },
    de: {
      title: 'Interessenskollision prüfen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      dateOfBirth: 'Geburtsdatum',
      email: 'E-Mail',
      street: 'Straße',
      houseNumber: 'Hausnr.',
      zip: 'PLZ',
      city: 'Stadt',
      checkConflict: 'Interessenkollision prüfen',
      warning: 'Vorsicht, hier könnte eine Interessenskollision vorliegen.',
      noConflict:
        'Anhand der übergebenen Parameter konnte keine Interessenskollision gefunden werden.',
      error:
        'Bei der Abfrage an Actaport ist leider ein Fehler aufgetreten. Bitte warten Sie einen Moment und versuchen es dann erneut. Sollte der Fehler weiterhin bestehen, wenden Sie sich bitte an den Support unter support@jupus.de.',
      caseFile: 'Akte',
      name: 'Name',
      role: 'Rolle',
      checkAnother: 'Erneut prüfen',
      tryAgain: 'Erneut versuchen',
      checkLabel: 'Prüfung:',
      client: 'Mandant',
      opponent: 'Gegner'
    }
  })

  // Store
  const caseStore = useCaseStore()
  const caseRecord = computed(() => caseStore.caseRecord)

  // State
  const loading = ref(false)
  const collisionResponse = ref<CollisionResponse | null>(null)
  const collisionResponseError = ref(false)
  const hasCheckedCollision = ref(false)
  const selectedPersonType = ref('opponent') // 'client' or 'opponent'

  // Options for person type selection
  const personTypeOptions = computed(() => [
    { value: 'client', title: t('client') },
    { value: 'opponent', title: t('opponent') }
  ])

  // Payload for the collision check
  const payload = ref<ActaportPayload>({
    firstName: '',
    lastName: '',
    email: '',
    street: '',
    streetNumber: '',
    zip: '',
    city: '',
    dateOfBirth: '',
    title: '',
    clerk: null,
    assistant: null,
    documents: [],
    phone: ''
  })

  // Computed property to get the current form data based on selected person type
  const formData = computed(() => {
    if (!caseRecord.value?.data) return null

    if (selectedPersonType.value === 'opponent') {
      return caseRecord.value.data.opponent
    } else {
      return caseRecord.value.data.client
    }
  })

  // Initialize payload with selected person data
  const initializePayload = () => {
    const data = formData.value
    if (!data) return

    payload.value = {
      firstName: data.first_name || '',
      lastName: data.last_name || '',
      email: data.email || '',
      street: data.street || '',
      streetNumber: data.street_number || '',
      zip: data.zip || '',
      city: data.city || '',
      dateOfBirth: data.date_of_birth || '',
      phone: data.phone || '',
      title: '',
      clerk: null,
      assistant: null,
      documents: []
    }
  }

  // Reset the form
  const resetForm = () => {
    hasCheckedCollision.value = false
    collisionResponse.value = null
    collisionResponseError.value = false
  }

  // Check for collision
  const checkCollision = async () => {
    if (!caseRecord.value?.id) {
      console.error('No case ID found')
      return
    }

    loading.value = true
    collisionResponseError.value = false
    collisionResponse.value = null

    try {
      const response = await ApiService.post(
        `/channels/export/actaport/cases/${caseRecord.value.id}/collisions`,
        payload.value,
        {},
        'v2'
      )

      console.log('Collision response:', response.data)
      collisionResponse.value = response.data
      hasCheckedCollision.value = true
      loading.value = false
    } catch (error) {
      console.error('Error checking for collision:', error)
      loading.value = false
      collisionResponseError.value = true
      hasCheckedCollision.value = true
    }
  }

  // Initialize payload when modal is opened
  onMounted(() => {
    initializePayload()
  })

  // Watch for changes in show prop to reset state
  watch(
    () => props.show,
    (newVal: boolean) => {
      if (newVal) {
        initializePayload()
        collisionResponse.value = null
        collisionResponseError.value = false
        hasCheckedCollision.value = false
      }
    }
  )

  // Watch for changes in selected person type to update payload
  watch(selectedPersonType, () => {
    initializePayload()
  })

  // Types
  interface ActaportPayload {
    firstName: string
    lastName: string
    email: string
    street: string
    streetNumber: string
    zip: string
    city: string
    title: string
    clerk: string | null
    assistant: string | null
    documents: any[]
    dateOfBirth: string
    phone: string
  }

  interface Collision {
    caseId: string
    caseTitle: string
    contact: string
    role: string
  }

  interface CollisionResponse {
    collisions: Collision[]
  }
</script>
